import { useState } from 'react';
import { Badge, Button, Input, Select } from '../../components/ui';
import {
  ROUND_ONE,
  endedReasonLabel,
  isWindowUnit,
  maxTouchesFor,
  paceIsPossible,
  roundErrorMessage,
  roundProgress,
  useLeadRounds,
  useRunningRounds,
  useStartLeadRound,
  windowEndOf,
  windowLabel,
  WINDOW_UNIT_OPTIONS,
} from './leadRounds';
import type { LeadRound, WindowUnit } from './leadRounds';
import { activityLabel, useLeadActivity } from './leadActivity';
import { activityDay, defaultStartsOn, previewAdoption } from './roundAdoption';
import { dayLabel, todayIso } from './trackerFormat';
import type { Lead } from './types';

/**
 * Where this lead is in the follow-up loop.
 *
 * Kyu's rule, which this section exists to make visible: "every lead should
 * always be scheduled or inside a round." Three permitted states and no
 * fourth — in a round, parked with the next one scheduled, or carrying an
 * overdue task to schedule one. So the section never renders nothing: a lead
 * with no round shows the way to start one.
 *
 * It does not END rounds and offers no control that would. The three endings
 * are the database's — the count is met, the window closes, they reply — and
 * a button here that stopped a round early would be a fourth way out of the
 * loop.
 *
 * ── Two queries, on purpose ────────────────────────────────────────────────
 *
 * `useRunningRounds` reads the whole `lead_round_state` view once for the
 * page, so the touch count and the window end come from the database rather
 * than being recomputed here. `useLeadRounds` is per-lead and fires when a
 * drawer opens; it is what knows the lead's history and therefore the next
 * round's number.
 *
 * The touch count in particular has to come from the view. The lead's own
 * `touch_count` spans every round it has ever been in, and using it would
 * report round four as finished before it began.
 */
export function LeadRoundBlock({
  lead,
  onError,
}: {
  lead: Lead;
  onError: (error: unknown) => void;
}) {
  const roundsQuery = useLeadRounds(lead.id);
  const runningQuery = useRunningRounds();
  // What has already been logged, so the round can claim it. Same query key as
  // the Activity tab, so opening a drawer that has one costs nothing extra.
  const activityQuery = useLeadActivity(lead.id);
  const startRound = useStartLeadRound();
  const [building, setBuilding] = useState(false);
  const [startsOn, setStartsOn] = useState(todayIso());
  const [target, setTarget] = useState(ROUND_ONE.touchTarget);
  const [length, setLength] = useState(ROUND_ONE.windowLength);
  const [unit, setUnit] = useState<WindowUnit>(ROUND_ONE.windowUnit);

  // Pending, not empty. "This lead is in no round" is a claim, and making it
  // for a moment on every drawer opened is how somebody starts a second round
  // on a lead that already has one.
  // Activity waits with the rest. The start date DEFAULTS to the oldest
  // unclaimed touch, so opening the form before the timeline has loaded would
  // quietly default to today and lose the very history this is here to catch —
  // and it would look identical to a lead who genuinely has none.
  if (roundsQuery.isPending || runningQuery.isPending || activityQuery.isPending) {
    return <p className="tracker-field-note">Checking rounds…</p>;
  }

  const activity = activityQuery.data ?? [];
  const rounds: LeadRound[] = roundsQuery.data ?? [];
  const running = runningQuery.data?.get(lead.id) ?? null;
  const lastEnded = rounds.find((round) => round.ended_on !== null) ?? null;
  const nextNumber = (rounds[0]?.number ?? 0) + 1;

  if (running !== null) {
    const progress = roundProgress({
      startsOn: running.startsOn,
      touchTarget: running.touchTarget,
      windowLength: running.windowLength,
      windowUnit: running.windowUnit,
      touchesDone: running.touchesDone,
      windowEnd: running.windowEnd,
      today: todayIso(),
    });
    return (
      <div className="lead-round">
        <p className="lead-round-title">
          Round {running.number} · {progress.touchesDone} of {progress.touchTarget} follow-ups
        </p>
        {/* dayLabel, never shortDate. Every date here is a date-only string,
            and shortDate renders one of those in the reader's own zone — a day
            early for everyone west of Greenwich, which is everyone on this
            team. trackerFormat.ts says so at length; this is a caller taking
            the advice. */}
        <p className="tracker-field-note">
          {windowLabel(running.windowLength, running.windowUnit)} from {dayLabel(running.startsOn)},
          through {dayLabel(progress.windowEnd)}.
        </p>
        {/* Facts, not a verdict. "Behind by 3" was here until 29 Aug, when Kyu
            rejected the frame rather than the number: a round is capped at one
            touch a day, so a 7-over-7 round that misses a day is permanently
            "behind" with no spare day to recover in. What is left and how long
            is left says the same thing when it is true, and says nothing when
            it is not. */}
        {progress.pace === 'not-started' ? (
          <Badge tone="neutral">Starts later</Badge>
        ) : progress.pace === 'running-out' ? (
          <Badge tone="warning">
            {`${String(progress.touchesLeft)} left, ${String(progress.daysToSendIn)} ${
              progress.daysToSendIn === 1 ? 'day' : 'days'
            }`}
          </Badge>
        ) : (
          <Badge tone="neutral">
            {`${String(progress.daysToSendIn)} ${
              progress.daysToSendIn === 1 ? 'day' : 'days'
            } left`}
          </Badge>
        )}
        {/* Named rather than implied. The three endings are the whole model,
            and somebody watching a round should know what they are waiting
            for. */}
        <p className="tracker-field-note">
          Ends when all {progress.touchTarget} are sent, when the window closes, or when they reply.
        </p>
      </div>
    );
  }

  const shape = { touchTarget: target, windowLength: length, windowUnit: unit };
  const possible = paceIsPossible(shape);
  const ceiling = maxTouchesFor(length, unit);
  // A date field can be EMPTY — every browser lets you clear one, and a person
  // retyping a date passes through empty on the way. `windowEndOf` throws on a
  // blank day, and both the window line and the claim block call it during
  // render, so without this the drawer disappears mid-keystroke and takes the
  // half-filled form with it. Found by a test that cleared the field; it has
  // been reachable since the builder shipped.
  const hasDate = /^\d{4}-\d{2}-\d{2}$/.test(startsOn);
  // Recomputed on every render rather than held in state, so it can never
  // disagree with the four fields above it — the drawer's own version of the
  // rule that a readout is derived and a setting is stored.
  const claiming = hasDate
    ? previewAdoption(activity, { startsOn, windowLength: length, windowUnit: unit }, target)
    : null;

  const submit = () => {
    if (!possible || !/^\d{4}-\d{2}-\d{2}$/.test(startsOn)) {
      return;
    }
    startRound.mutate(
      {
        leadId: lead.id,
        startsOn,
        touchTarget: target,
        windowLength: length,
        windowUnit: unit,
      },
      {
        onSuccess: () => {
          setBuilding(false);
        },
        onError: (error) => {
          onError(new Error(roundErrorMessage(error instanceof Error ? error.message : '')));
        },
      },
    );
  };

  if (!building) {
    return (
      <div className="lead-round">
        {lastEnded === null ? (
          <p className="tracker-field-note">This lead has never been in a round.</p>
        ) : (
          <p className="tracker-field-note">
            Round {lastEnded.number} ended {dayLabel(lastEnded.ended_on ?? '')} —{' '}
            {endedReasonLabel(lastEnded.ended_reason)}.
          </p>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            // Round one is the preset; later rounds start from it and get
            // changed, because Kyu has no standard shape for them yet — "we
            // don't have a definite structure on how many days and follow ups
            // we're gonna do on the next rounds".
            // Not today — the day of the oldest email nobody has counted yet,
            // within the last 30 days. Jacob Davis had four logged by Hanneza
            // on 25 and 27 August and would otherwise have started at 0 of 7
            // and been sent seven more.
            setStartsOn(defaultStartsOn(activity, todayIso()));
            setTarget(ROUND_ONE.touchTarget);
            setLength(ROUND_ONE.windowLength);
            setUnit(ROUND_ONE.windowUnit);
            setBuilding(true);
          }}
        >
          Start round {nextNumber}
        </Button>
      </div>
    );
  }

  return (
    <div className="lead-round">
      <Input
        label="Starts on"
        type="date"
        value={startsOn}
        onChange={(event) => {
          setStartsOn(event.target.value);
        }}
      />
      <Input
        label="Follow-ups"
        type="number"
        min={1}
        max={ceiling}
        value={String(target)}
        hint={`At most ${String(ceiling)} — one a day is the ceiling.`}
        error={possible ? undefined : 'More than one a day. Lengthen the window or ask for fewer.'}
        onChange={(event) => {
          setTarget(Number(event.target.value));
        }}
      />
      <Input
        label="Window"
        type="number"
        min={1}
        value={String(length)}
        onChange={(event) => {
          setLength(Number(event.target.value));
        }}
      />
      <Select
        label="Counted in"
        value={unit}
        options={WINDOW_UNIT_OPTIONS.map((option) => ({
          value: option.value,
          label: option.label,
        }))}
        hint="Business days skip weekends, so a weekend is never a missed day."
        onChange={(event) => {
          const next = event.target.value;
          if (isWindowUnit(next)) {
            setUnit(next);
          }
        }}
      />
      {/* Shown before the round exists, because "when does this end" is the
          question somebody is actually answering when they pick a window. */}
      <p className="tracker-field-note">
        {hasDate
          ? `Runs through ${dayLabel(windowEndOf(startsOn, length, unit))}.`
          : 'Pick a start date.'}
      </p>
      {/* What this round will pick up from what already happened.
          A readout, not a setting: move the date above and this changes. It is
          rendered only when there IS something, so a lead with no history sees
          the form exactly as it was before this existed. */}
      {claiming !== null && claiming.claimed.length > 0 ? (
        <div className="round-claim">
          <p className="round-claim-head">
            {claiming.touches === 1
              ? '1 email already sent counts toward this round'
              : `${String(claiming.touches)} emails already sent count toward this round`}
          </p>
          <ul className="round-claim-list">
            {claiming.claimed.map((entry) => (
              <li key={entry.id}>
                {dayLabel(activityDay(entry.occurred_at))} — {activityLabel(entry.kind)}
              </li>
            ))}
          </ul>
          <p className="round-claim-count">
            Starts at {String(claiming.touches)} of {String(target)} · {String(claiming.left)} left
            to send
          </p>
          {/* The one surprise worth spelling out. The database ends a round on
              the spot when its claimed history already met the target or
              carried a reply, so a button reading "Start round 1" would
              otherwise start and close one in the same breath. */}
          {claiming.endsImmediately ? (
            <p className="round-claim-warning">
              {claiming.endsBecause === 'replied'
                ? 'They have already replied, so this round will close as soon as it starts.'
                : 'That already meets the target, so this round will close as soon as it starts.'}
            </p>
          ) : null}
        </div>
      ) : null}
      <Button size="sm" disabled={!possible || !hasDate || startRound.isPending} onClick={submit}>
        {startRound.isPending ? 'Starting…' : `Start round ${String(nextNumber)}`}
      </Button>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          setBuilding(false);
        }}
      >
        Cancel
      </Button>
    </div>
  );
}
